import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowRightIcon, X } from 'lucide-react';

interface EthnicityOption {
    label: string;
    value: string;
}

interface EthnicityInputProps {
    name: string;
    selected?: string[];
    options: EthnicityOption[];
    limit?: number;
    label?: string;
    required?: boolean;
}

const EthnicityInput: React.FC<EthnicityInputProps> = ({
    name,
    selected = [],
    options,
    limit = 5,
    label = 'Ethnicity',
    required = false
}) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(selected);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const [openUpward, setOpenUpward] = useState(false);

    // Filter options based on search query and exclude already selected
    const filteredOptions = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        return options.filter(option =>
            !selectedValues.includes(option.value) &&
            (query === '' || option.label.toLowerCase().includes(query))
        );
    }, [options, selectedValues, searchQuery]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const updateDropdownDirection = () => {
        if (searchContainerRef.current) {
            const rect = searchContainerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 240; // max-h-60
            setOpenUpward(spaceBelow < dropdownHeight + 20 && rect.top > dropdownHeight);
        }
    };

    useEffect(() => {
        if (showDropdown) {
            updateDropdownDirection();
            window.addEventListener('scroll', updateDropdownDirection, true);
            window.addEventListener('resize', updateDropdownDirection);
        }
        return () => {
            window.removeEventListener('scroll', updateDropdownDirection, true);
            window.removeEventListener('resize', updateDropdownDirection);
        };
    }, [showDropdown]);

    const handleSelect = (value: string) => {
        if (selectedValues.length >= limit) return;

        setSelectedValues([...selectedValues, value]);
        setSearchQuery('');
        setShowDropdown(false);

        // Focus back on input
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleRemove = (value: string) => {
        setSelectedValues(selectedValues.filter(v => v !== value));
    };

    const hasError = required && selectedValues.length === 0;

    return (
        <div className={`rounded-xl border bg-grayish-blue-80 px-4 py-2-1 ${hasError ? 'border-red-500 is-invalid' : 'border-gray-300'}`}
            style={hasError ? { borderColor: '#dc2626' } : {}}
            onClick={() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }}>

            {/* Hidden inputs for Rails form submission */}
            {selectedValues.map(value => (
                <input key={value} type="hidden" name={name} value={value} />
            ))}
            {/* If no values selected and required, we might need a way to trigger browser validation or handle it via a hidden input with required */}
            <input
                type="text"
                required={required}
                value={selectedValues.length > 0 ? 'valid' : ''}
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none', zIndex: -1 }}
                onChange={() => { }}
                onInvalid={(e) => {
                    e.preventDefault();
                    // The parent form/controller usually handles the error class toggling based on the container's state,
                    // but here we rely on the container's `hasError` styled by backend state mostly.
                    // For client-side interactivity, strict "required" blocking might impede UX if not handled carefully with custom validation.
                    // We'll leave this simple for now. 
                }}
            />

            <label className="block text-xs font-medium text-gray-600 mb-1 px-0">
                {label}{required ? '*' : ''}
            </label>

            <div className="flex flex-col gap-2-1" ref={dropdownRef}>
                <div className="flex flex-wrap gap-3 items-center relative">

                    {/* Selected Tags */}
                    {selectedValues.map(value => {
                        const option = options.find(o => o.value === value);
                        return (
                            <div
                                key={value}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-300 hover:border-gray-400 transition-colors"
                                onClick={(e) => e.stopPropagation()} // Prevent triggering parent click focus
                            >
                                <span className="text-sm text-gray-700">{option?.label || value}</span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(value);
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}

                    {/* Search Input */}
                    {selectedValues.length < limit && (
                        <div className="inline-flex relative flex-1 min-w-full" ref={searchContainerRef}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                placeholder={selectedValues.length === 0 ? 'Select ethnicities...' : ''}
                                className="text-sm bg-transparent border-0 outline-none focus:ring-0 focus:border-0 text-gray-700 placeholder-gray-400 w-full p-0"
                            />

                            {/* Dropdown */}
                            {showDropdown && filteredOptions.length > 0 && (
                                <div
                                    className={`absolute z-10 bg-white rounded-lg max-h-60 overflow-y-auto p-3 shadow-lg ring-1 ring-black ring-opacity-5 ${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'}`}
                                    style={{
                                        minWidth: '12.5rem',
                                        width: '100%'
                                    }}
                                >
                                    {filteredOptions.map((option) => (
                                        <div
                                            key={option.value}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(option.value);
                                            }}
                                            className="flex items-center justify-start px-4 py-2 hover:bg-blue-50 cursor-pointer gap-2 rounded-md transition-colors"
                                        >
                                            <div className="w-4 h-4 flex-shrink-0">
                                                <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <div className="text-sm font-medium text-gray-700">
                                                {option.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showDropdown && filteredOptions.length === 0 && searchQuery !== '' && (
                                <div className={`absolute z-10 bg-white rounded-lg p-3 shadow-lg ring-1 ring-black ring-opacity-5 ${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'}`} style={{ minWidth: '12.5rem' }}>
                                    <span className="text-sm text-gray-500">No results found</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-between items-center mt-1">
                    <div className="text-xs text-gray-500">
                        {selectedValues.length >= limit && <span className="text-amber-600">Max {limit} selected</span>}
                    </div>
                    <div className="text-xs text-gray-500 text-right">({selectedValues.length}/{limit})</div>
                </div>
            </div>
        </div>
    );
};

export default EthnicityInput;
